import * as R from 'ramda';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature configs
import { CONFIG_PAGE_FORM } from './constants';
//////////////////////////////////////////////////

const selectConfigStore = (state) => state.config;

// COMMON
const makeSelectCurrentGroupName = () => createSelector(
  selectConfigStore,
  ({ currentGroupName }) => currentGroupName,
);

const makeSelectFinancial = () => createSelector(
  selectConfigStore,
  ({ financial }) => financial,
);

const makeSelectConfigByName = (configName) => createSelector(
  [
    selectConfigStore,
    R.path(['config', 'currentGroupName']),
  ],
  (state, currentGroupName) => R.or(
    R.path([currentGroupName, 'configs', configName], state),
    R.path([currentGroupName, 'dropdowns', configName], state),
  ),
);

const makeSelectConfigInitialValues = createCachedSelector(
  selectConfigStore,
  (store, name) => name,
  (store, name) => R.path([name, 'initialValues'], store),
)(
  (store, name) => name,
);

const makeSelectConfigInheritedValues = createCachedSelector(
  selectConfigStore,
  (store, name) => name,
  (store, name) => R.path([name, 'inheritedValues'], store),
)(
  (store, name) => name,
);

const makeSelectConfigGuidsToItemsMap = createCachedSelector(
  selectConfigStore,
  (store, name) => name,
  (store, name) => R.path([name, 'guidsToItemsMap'], store),
)(
  (store, name) => name,
);

const makeSelectConfigOptions = createCachedSelector(
  selectConfigStore,
  (store, name) => name,
  (store, name) => R.path([name, 'options'], store),
)(
  (store, name) => name,
);

const makeSelectConfigDropdowns = createCachedSelector(
  selectConfigStore,
  (store, name) => name,
  (store, name) => R.path([name, 'dropdowns'], store),
)(
  (store, name) => name,
);

const makeSelectConfigWarning = createCachedSelector(
  selectConfigStore,
  (store, name) => name,
  (store, name) => R.path([name, 'warning'], store),
)(
  (store, name) => name,
);

const makeSelectTabsVisited = () => createSelector(
  selectConfigStore,
  ({ currentGroupName, ...config }) => R.pathOr([], [currentGroupName, 'tabsVisited'], config),
);

const makeSelectActiveGroupWithTab = () => createSelector(
  selectConfigStore,
  ({ activeGroupWithTab }) => activeGroupWithTab,
);

const makeSelectFleetDocumentAndCertificationTypes = () => createSelector(
  selectConfigStore,
  ({ truck, driver, trailer }) => ({
    [GC.TRUCK_DOCUMENT_TYPE]: R.pathOr(null, ['dropdowns', GC.TRUCK_DOCUMENT_TYPE], truck),
    [GC.DRIVER_DOCUMENT_TYPE]: R.pathOr(null, ['dropdowns', GC.DRIVER_DOCUMENT_TYPE], driver),
    [GC.TRAILER_DOCUMENT_TYPE]: R.pathOr(null, ['dropdowns', GC.TRAILER_DOCUMENT_TYPE], trailer),
    [GC.DRIVER_CERTIFICATION_TYPE]: R.pathOr(null, ['dropdowns', GC.DRIVER_CERTIFICATION_TYPE], driver),
  }),
);
// COMMON

const selectCLOStore = (state) => state.config.clo;
const selectTELStore = (state) => state.config.tel;
const selectUIConfigStore = (state) => state.config.ui;
const selectTruckConfigStore = (state) => state.config.truck;
const selectDriverConfigStore = (state) => state.config.driver;
const selectGeneralConfigStore = (state) => state.config.general;
const selectTrailerConfigStore = (state) => state.config.trailer;
const selectCarrierConfigStore = (state) => state.config.carrier;
const selectInvoicesConfigStore = (state) => state.config.invoice;
const selectRateEngineConfigStore = (state) => state.config.rateEngine;
const selectTaskManagementStore = (state) => state.config.taskManagement;
export const selectIntegrationStore = (state) => state.config.integration;
const selectCommunicationConfigStore = (state) => state.config.communication;

const makeSelectAccessorialsList = () => createSelector(
  selectGeneralConfigStore,
  ({ accessorialsList }) => accessorialsList,
);

const makeSelectDistanceCalculatorConfigList = () => createSelector(
  selectGeneralConfigStore,
  ({ distanceCalculatorConfigList }) => distanceCalculatorConfigList,
);

const makeSelectDeactivationRuleList = () => createSelector(
  selectCarrierConfigStore,
  ({ deactivationRuleList }) => deactivationRuleList,
);

const makeSelectOnboardingPackageList = () => createSelector(
  selectCarrierConfigStore,
  ({ onboardingPackageList }) => onboardingPackageList,
);

const makeSelectOnboardingPackageDocumentList = () => createSelector(
  selectCarrierConfigStore,
  ({ onboardingPackageDocumentList }) => R.values(onboardingPackageDocumentList),
);

const makeSelectTermsAndConditionsDocument = () => createSelector(
  selectCarrierConfigStore,
  ({ termsAndConditionsDocument }) => termsAndConditionsDocument,
);

const makeSelectOrderQuoteTermsAndConditionsDocument = () => createSelector(
  selectCLOStore,
  ({ termsAndConditionsDocument }) => termsAndConditionsDocument,
);

const makeSelectUserTermsAndConditionsDocument = () => createSelector(
  selectGeneralConfigStore,
  ({ termsAndConditionsDocument }) => termsAndConditionsDocument,
);

const makeSelectDriverAppAcceptRateTermsAndConditionsDocument = () => createSelector(
  selectDriverConfigStore,
  ({ termsAndConditionsDocument }) => termsAndConditionsDocument,
);

const makeSelectCustomerInvoiceStatuses = () => createSelector(
  selectInvoicesConfigStore,
  ({ customerInvoiceStatuses }) => customerInvoiceStatuses,
);

const makeSelectDriverAppAcceptPayrollTermsAndConditionsDocument = () => createSelector(
  selectInvoicesConfigStore,
  ({ termsAndConditionsDocument }) => termsAndConditionsDocument,
);

const makeSelectCarrierInvoiceStatuses = () => createSelector(
  selectInvoicesConfigStore,
  ({ carrierInvoiceStatuses }) => R.map((item) => R.mergeRight(
    item,
    {
      [GC.FIELD_LABEL]: G.getPropFromObject(GC.FIELD_DISPLAYED_VALUE, item),
      [GC.FIELD_VALUE]: G.getPropFromObject(GC.FIELD_ORIGINAL_CONFIG_GUID, item),
    },
  ), carrierInvoiceStatuses),
);

const makeSelectFleetServiceInvoiceStatuses = () => createSelector(
  selectInvoicesConfigStore,
  ({ fleetServiceInvoiceStatuses }) => fleetServiceInvoiceStatuses,
);

const makeSelectDriverPayrollStatuses = () => createSelector(
  selectInvoicesConfigStore,
  ({ driverPayrollStatuses }) => driverPayrollStatuses,
);

const makeSelectConnectedToQB = () => createSelector(
  selectInvoicesConfigStore,
  (state) => ({
    ownAuth: R.path(['initialValues', GC.INVOICE_QB_AUTHORIZATION], state),
    parentAuth: R.path(['inheritedValues', GC.INVOICE_QB_AUTHORIZATION], state),
  }),
);

const makeSelectQBAccountMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ qbAccountMappingList, qbAccountMappingFilter }) => R.compose(
    R.filter(({ scope }) => R.or(
      G.isNilOrEmpty(qbAccountMappingFilter),
      R.equals(qbAccountMappingFilter, scope),
    )),
    R.flatten,
    R.values,
  )(R.or(qbAccountMappingList, {})),
);

const makeSelectQBCustomFieldMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ qbCustomFieldMappingList }) => qbCustomFieldMappingList,
);

const makeSelectQBTaxCodeMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ qbTaxCodeMappingList }) => qbTaxCodeMappingList,
);

const makeSelectQbIffAssessorialMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ qbIffAssessorialMappingList, qbIffAssessorialMappingFilter }) => R.compose(
    R.filter(({ scope }) => R.or(
      G.isNilOrEmpty(qbIffAssessorialMappingFilter),
      R.equals(qbIffAssessorialMappingFilter, scope),
    )),
    R.flatten,
    R.values,
  )(R.or(qbIffAssessorialMappingList, {})),
);

const makeSelectQbDesktopConnectionList = () => createSelector(
  selectInvoicesConfigStore,
  ({ qbDesktopConnectionList }) => qbDesktopConnectionList,
);

const makeSelectPropelHRAssessorialMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ propelHRAssessorialMappingList }) => propelHRAssessorialMappingList,
);

const makeSelectPaylocityCodeMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ paylocityCodeMappingList }) => paylocityCodeMappingList,
);

const makeSelectFuelCardTransactionMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ fuelCardTransactionMappingList }) => fuelCardTransactionMappingList,
);

const makeSelectSageAccountMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ sageAccountMappingList, sageAccountMappingFilter }) => R.compose(
    R.filter(({ scope }) => R.or(
      G.isNilOrEmpty(sageAccountMappingFilter),
      R.equals(sageAccountMappingFilter, scope),
    )),
    R.flatten,
    R.values,
  )(R.or(sageAccountMappingList, {})),
);

const makeSelectCustomerIdMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ customerIdMappingList }) => customerIdMappingList,
);

const makeSelectDepartmentIdMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ departmentIdMappingList }) => departmentIdMappingList,
);

const makeSelectGLCodeMappingList = () => createSelector(
  selectInvoicesConfigStore,
  ({ glCodeMappingList, glCodeMappingFilter }) => R.compose(
    R.filter(({ scope }) => R.or(
      G.isNilOrEmpty(glCodeMappingFilter),
      R.equals(glCodeMappingFilter, scope),
    )),
    R.flatten,
    R.values,
  )(R.or(glCodeMappingList, {})),
);

const makeSelectInvoiceStatusRuleList = () => createSelector(
  selectInvoicesConfigStore,
  ({ invoiceStatusRuleList, invoiceStatusRuleFilter }) => R.compose(
    R.sortBy(R.prop(GC.FIELD_PRIORITY)),
    R.filter(({ scope }) => R.or(
      G.isNilOrEmpty(invoiceStatusRuleFilter),
      R.equals(invoiceStatusRuleFilter, scope),
    )),
    R.flatten,
    R.values,
  )(R.or(invoiceStatusRuleList, {})),
);

const makeSelectInvoiceStatusPriorityList = () => createSelector(
  selectInvoicesConfigStore,
  ({ invoiceStatusRuleList }) => {
    const length = R.compose(
      R.inc,
      R.reduce((acc, { priority }) => G.ifElse(R.gt(priority, acc), priority, acc), 0),
      R.filter(R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID)),
      R.flatten,
      R.values,
    )(invoiceStatusRuleList);

    const priorityList = Array.from(
      { length },
      (_, index) => ({ label: R.inc(index), value: R.inc(index) }),
    );

    return priorityList;
  },
);

const makeSelectQBAccountMappingFilter = () => createSelector(
  selectInvoicesConfigStore,
  ({ qbAccountMappingFilter }) => qbAccountMappingFilter,
);

const makeSelectSageAccountMappingFilter = () => createSelector(
  selectInvoicesConfigStore,
  ({ sageAccountMappingFilter }) => sageAccountMappingFilter,
);

const makeSelectGLCodeMappingFilter = () => createSelector(
  selectInvoicesConfigStore,
  ({ glCodeMappingFilter }) => glCodeMappingFilter,
);

const makeSelectInvoiceStatusRuleFilter = () => createSelector(
  selectInvoicesConfigStore,
  ({ invoiceStatusRuleFilter }) => invoiceStatusRuleFilter,
);

const makeSelectExpenseTypeList = () => createSelector(
  selectInvoicesConfigStore,
  ({ expenseTypeList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(expenseTypeList),
);

const makeSelectStatusMessagesList = () => createSelector(
  selectCommunicationConfigStore,
  ({ statusMessages }) => statusMessages,
);

const makeSelectServicesOptions = () => createSelector(
  selectGeneralConfigStore,
  (configStore) => G.getFullOptionsFromDropdownConfigStore(
    configStore,
    GC.GENERAL_SERVICES,
  ),
);

const makeSelectReasonCodesOptions = () => createSelector(
  selectCommunicationConfigStore,
  (configStore) => G.getFullOptionsFromDropdownConfigStore(
    configStore,
    GC.COMMUNICATION_REASON_CODE,
  ),
);

const makeSelectNotificationTriggers = () => createSelector(
  selectCommunicationConfigStore,
  ({ notificationConfig }) => notificationConfig,
);

const makeSelectNotificationListTriggers = () => createSelector(
  selectCommunicationConfigStore,
  ({ notificationList }) => notificationList,
);

const makeSelectFilteredNotificationList = () => createSelector(
  selectCommunicationConfigStore,
  ({ notificationList, notificationTriggersFilter, notificationObjectTypeFilter }) => {
    const filterByObjectType = R.filter(
      ({ objectType }) => R.or(
        G.isNilOrEmpty(notificationObjectTypeFilter),
        R.equals(objectType, notificationObjectTypeFilter),
      ),
    );

    const filterByTrigger = R.filter(
      ({ trigger }) => R.or(
        G.isNilOrEmpty(notificationTriggersFilter),
        R.equals(trigger, notificationTriggersFilter),
      ),
    );

    return R.compose(
      filterByObjectType,
      filterByTrigger,
      R.values,
    )(notificationList);
  },
);

const makeSelectNotificationTriggersFilter = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr('', ['notificationTriggersFilter']),
);

const makeSelectNotificationObjectTypeFilter = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr('', ['notificationObjectTypeFilter']),
);

const makeSelectFuelCardList = () => createSelector(
  selectIntegrationStore,
  ({ fuelCardList }) => fuelCardList,
);

const makeSelectEdiConfigsList = () => createSelector(
  selectIntegrationStore,
  ({ ediConfigsList }) => ediConfigsList,
);

const makeSelectEdiExtractorConfigList = () => createSelector(
  selectIntegrationStore,
  ({ ediExtractorConfigList }) => ediExtractorConfigList,
);

const makeSelectEdiExporterConfigList = () => createSelector(
  selectIntegrationStore,
  ({ ediExporterConfigList }) => ediExporterConfigList,
);

const makeSelectIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  ({ integrationConfigList }) => integrationConfigList,
);

const makeSelectCarrierRateIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  ({ carrierRateIntegrationList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(carrierRateIntegrationList),
);

const makeSelectUserMonitoringIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  ({ userMonitoringIntegrationList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(userMonitoringIntegrationList),
);


const makeSelectServiceMappingConfigList = () => createSelector(
  selectIntegrationStore,
  ({ serviceMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(serviceMappingList),
);

const makeSelectFilteredServiceMappingConfigList = () => createSelector(
  selectIntegrationStore,
  ({ serviceMappingList, serviceMappingFilter }) => R.filter(
    ({ integrationType }) => R.or(
      G.isNilOrEmpty(serviceMappingFilter),
      R.equals(integrationType, serviceMappingFilter),
    ),
    R.values(serviceMappingList),
  ),
);

const makeSelectServiceMappingFilter = () => createSelector(
  selectIntegrationStore,
  R.pathOr('', ['serviceMappingFilter']),
);

const makeSelectAdvancePaymentList = () => createSelector(
  selectIntegrationStore,
  ({ advancePaymentList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(advancePaymentList),
);

const makeSelectLoadBoardConfigList = () => createSelector(
  selectIntegrationStore,
  ({ loadBoardConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(loadBoardConfigList),
);

const makeSelectTollChargeList = () => createSelector(
  selectIntegrationStore,
  ({ tollChargeList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(tollChargeList),
);

const makeSelectGpsIntegrationList = () => createSelector(
  selectIntegrationStore,
  ({ gpsIntegrationList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(gpsIntegrationList),
);

const makeSelectDispatchIntegrationList = () => createSelector(
  selectIntegrationStore,
  ({ dispatchIntegrationList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(dispatchIntegrationList),
);

const makeSelectServicesForAccessorials = () => createSelector(
  selectGeneralConfigStore,
  (configStore) => {
    let options = [];
    let optionsGuids = [];
    const configValue = R.pathOr(null, ['dropdowns', GC.GENERAL_SERVICES, 'options'], configStore);
    if (G.isNotNil(configValue)) {
      options = configValue.map((item) => ({
        label: `${item.displayedValue} (${item.storedValue})`,
        value: R.or(R.prop('parentGuid', item), R.prop('guid', item)),
      }));
      optionsGuids = configValue.map((item) => R.or(
        R.prop('parentGuid', item),
        R.prop('guid', item),
      ));
    }
    return {
      options: ['', ...options],
      optionsGuids,
    };
  },
);

const makeSelectReasonsForStatusCode = () => createSelector(
  selectCommunicationConfigStore,
  (configStore) => {
    let multiOptions = [];
    let reasonGuids = [];

    const configValue = R.pathOr(
      null,
      ['dropdowns', GC.COMMUNICATION_REASON_CODE, 'options'],
      configStore,
    );

    if (G.isNotNil(configValue)) {
      multiOptions = R.map(({ guid, storedValue, parentGuid, displayedValue }) => ({
        label: `${displayedValue} (${storedValue})`,
        value: R.or(parentGuid, guid),
      }), configValue);

      reasonGuids = R.map(({ guid, parentGuid }) => R.or(parentGuid, guid), configValue);
    }

    return { multiOptions, reasonGuids };
  },
);

const makeSelectDocumentList = () => createSelector(
  selectCommunicationConfigStore,
  (configStore) => configStore.documentsConfig,
);

const makeSelectDataSourceList = () => createSelector(
  selectCommunicationConfigStore,
  ({ dataSourceList }) => dataSourceList,
);

const makeSelectFilteredDocumentList = () => createSelector(
  selectCommunicationConfigStore,
  ({ documentsConfig, documentsFilter }) => R.filter(
    ({ dataSource }) => R.or(
      G.isNilOrEmpty(documentsFilter),
      R.equals(dataSource, documentsFilter),
    ),
    R.values(documentsConfig),
  ),
);

const makeSelectDocumentFilter = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr('', ['documentsFilter']),
);

const makeSelectIndexedDataSourceList = () => createSelector(
  selectCommunicationConfigStore,
  ({ indexedDataSourceList }) => indexedDataSourceList,
);

const makeSelectDocumentTypes = () => createSelector(
  selectCommunicationConfigStore,
  (configStore) => ({
    documentTypeGuid: G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      configStore, GC.COMMUNICATION_DOCUMENT_TYPE, true,
    ),
  }),
);

const makeSelectAvailableDocumentTypes = () => createSelector(
  selectCommunicationConfigStore,
  ({ availableDocumentTypes }) => availableDocumentTypes,
);

const makeSelectCloDispatchBoardActionPanel = () => createSelector(
  selectUIConfigStore,
  ({ cloDispatchBoardActionPanel }) => R.or(cloDispatchBoardActionPanel, []),
);

const makeSelectTelDispatchBoardActionPanel = () => createSelector(
  selectUIConfigStore,
  ({ telDispatchBoardActionPanel }) => R.or(telDispatchBoardActionPanel, []),
);

const makeSelectCentralizedDispatchActionPanel = () => createSelector(
  selectUIConfigStore,
  ({ centralizedDispatchActionPanel }) => R.or(centralizedDispatchActionPanel, []),
);

const makeSelectStatusCodeMappingList = () => createSelector(
  selectIntegrationStore,
  ({ statusCodeMappingList }) => R.values(statusCodeMappingList),
);

const makeSelectStatusReasonCodeMappingList = () => createSelector(
  selectIntegrationStore,
  ({ statusReasonCodeMappingList }) => R.values(statusReasonCodeMappingList),
);

const makeSelectStatusReasonCodeOutboundMappingList = () => createSelector(
  selectIntegrationStore,
  ({ statusReasonCodeOutboundMappingList }) => R.values(statusReasonCodeOutboundMappingList),
);

const makeSelectPublicCLOConfig = () => createSelector(
  selectIntegrationStore,
  ({ publicCLOConfig }) => publicCLOConfig,
);

const makeSelectServiceTypeMappingList = () => createSelector(
  selectIntegrationStore,
  ({ serviceTypeMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(serviceTypeMappingList),
);

const makeSelectDefaultOrderAccessorialList = () => createSelector(
  selectCLOStore,
  ({ defaultOrderAccessorialList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(defaultOrderAccessorialList),
);

const makeSelectDefaultCarrierAccessorialList = () => createSelector(
  selectTELStore,
  ({ defaultCarrierAccessorialList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(defaultCarrierAccessorialList),
);

const makeSelectCarrierSynchronizationConfig = () => createSelector(
  selectIntegrationStore,
  ({ carrierSynchronization }) => carrierSynchronization,
);

const makeSelectCarrierIntegrationOverrideList = () => createSelector(
  selectIntegrationStore,
  ({ carrierIntegrationOverrideList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(carrierIntegrationOverrideList),
);

const makeSelectIntegrationMailConfig = () => createSelector(
  selectCommunicationConfigStore,
  ({ integrationMailConfig }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(integrationMailConfig),
);

const makeSelectDocumentTypeMappingList = () => createSelector(
  selectIntegrationStore,
  ({ documentTypeMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(documentTypeMappingList),
);

const makeSelectOutboundDocumentTypeMappingList = () => createSelector(
  selectIntegrationStore,
  ({ outboundDocumentTypeMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(outboundDocumentTypeMappingList),
);

const makeSelectDocumentHubConfigList = () => createSelector(
  selectIntegrationStore,
  ({ documentHubConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(documentHubConfigList),
);

const makeSelectAccountingConfigList = () => createSelector(
  selectIntegrationStore,
  ({ accountingConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(accountingConfigList),
);

const makeSelectCarrierSequences = () => createSelector(
  selectIntegrationStore,
  ({ carrierSequences }) => carrierSequences,
);

const makeSelectCarrierPickupRequesterListList = () => createSelector(
  selectIntegrationStore,
  ({ carrierPickupRequesterList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(carrierPickupRequesterList),
);

const makeSelectCarrierEdiLoaderConfigList = () => createSelector(
  selectIntegrationStore,
  ({ carrierEdiLoaderConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(carrierEdiLoaderConfigList),
);

const makeSelectCarrierEdiExporterConfigList = () => createSelector(
  selectIntegrationStore,
  ({ carrierEdiExporterConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(carrierEdiExporterConfigList),
);

const makeSelectTransportationModeConfigMappingList = () => createSelector(
  selectGeneralConfigStore,
  ({ transportationModeConfigMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(transportationModeConfigMappingList),
);

const makeSelectIntegrationTransportationModeConfigMappingList = () => createSelector(
  selectIntegrationStore,
  ({ transportationModeConfigMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(transportationModeConfigMappingList),
);

const makeSelectVinLookupConfigList = () => createSelector(
  selectIntegrationStore,
  ({ vinLookupConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(vinLookupConfigList),
);

const makeSelectLoadCustomStatusConfigList = () => createSelector(
  selectGeneralConfigStore,
  ({ loadCustomStatusConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(loadCustomStatusConfigList),
);

const makeSelectFactoringIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  ({ factoringIntegrationConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(factoringIntegrationConfigList),
);

const makeSelectEquipmentMappingConfigList = () => createSelector(
  selectIntegrationStore,
  ({ equipmentMappingConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(equipmentMappingConfigList),
);

const makeSelectCommunicationMails = () => createSelector(
  selectCommunicationConfigStore,
  R.compose(
    R.filter(G.isNotNilAndNotEmpty),
    R.pick([GC.COMMUNICATION_BRANCH_EMAIL, GC.COMMUNICATION_ACCOUNTING_DEPARTMENT_EMAIL]),
    R.pathOr({}, ['initialValues']),
  ),
);

const makeSelectStatusCodeOutboundMappingList = () => createSelector(
  selectIntegrationStore,
  ({ statusCodeOutboundMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(statusCodeOutboundMappingList),
);

const makeSelectMarginViolationRuleList = () => createSelector(
  selectRateEngineConfigStore,
  ({ marginViolationRuleList }) => marginViolationRuleList,
);

const makeSelectOnboardingIntegrationList = () => createSelector(
  selectDriverConfigStore,
  ({ onboardingIntegrationList }) => onboardingIntegrationList,
);

const makeSelectTrailerTrackingIntegrationList = () => createSelector(
  selectIntegrationStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['trailerTrackingIntegrationList']),
  ),
);

const makeSelectLosSubscriptionConfig = () => createSelector(
  selectIntegrationStore,
  ({ losSubscriptionConfig }) => losSubscriptionConfig,
);

const makeSelectLosPricingPlanList = () => createSelector(
  selectIntegrationStore,
  ({ losPricingPlanList }) => R.compose(G.sortByIndex, R.values)(losPricingPlanList),
);

const makeSelectCarrierScoreCardConfigList = () => createSelector(
  selectCarrierConfigStore,
  R.pathOr([], ['carrierScorecardConfigList']),
);

const makeSelectTerminalIntegrationList = () => createSelector(
  selectIntegrationStore,
  ({ terminalIntegrationList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(terminalIntegrationList),
);

const makeSelectMailTemplateConfig = () => createSelector(
  selectCommunicationConfigStore,
  ({ mailTemplateConfig }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(mailTemplateConfig),
);

const makeSelectMailTemplateTypes = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr([], ['mailTemplateTypes']),
);

const makeSelectMailTemplateNames = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr([], ['mailTemplateNames']),
);

const makeSelectMailTemplateByKey = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr({}, ['mailTemplateByKey']),
);

const makeSelectTaskTypeList = () => createSelector(
  selectTaskManagementStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['taskTypeList']),
  ),
);

const makeSelectTaskStatusList = () => createSelector(
  selectTaskManagementStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['taskStatusList']),
  ),
);

const makeSelectDocumentSendingList = () => createSelector(
  selectCommunicationConfigStore,
  R.pathOr([], ['documentSendingList']),
);

const makeSelectTransportationModeGroupingList = () => createSelector(
  selectGeneralConfigStore,
  R.pathOr([], ['transportationModeGroupingList']),
);

const makeSelectCrossBorderIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['crossBorderIntegrationConfigList']),
  ),
);

const makeSelectTruckTypeMappingList = () => createSelector(
  selectIntegrationStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['truckTypeMappingList']),
  ),
);

const makeSelectTrailerTypeMappingList = () => createSelector(
  selectIntegrationStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['trailerTypeMappingList']),
  ),
);

// equipment service auto create
const makeSelectServiceVendorOptions = createSelector(
  R.path(['config', 'serviceVendor', 'options', 'serviceVendors']),
  (serviceVendors) => serviceVendors,
);

const makeSelectServiceTypeOptions = createSelector(
  R.path(['config', 'fleetGeneral', 'options', 'serviceTypes']),
  (serviceTypes) => serviceTypes,
);

const makeSelectTruckEquipmentServiceAutoCreateConfigList = () => createSelector(
  selectTruckConfigStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['equipmentServiceAutoCreateConfigList']),
  ),
);

const makeSelectTrailerEquipmentServiceAutoCreateConfigList = () => createSelector(
  selectTrailerConfigStore,
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.pathOr({}, ['equipmentServiceAutoCreateConfigList']),
  ),
);

const makeSelectHidiByConfigMapForAuthWrapper = () => createSelector(
  selectConfigStore,
  ({ invoice }) => {
    const { qbDesktopConnectionList } = invoice;

    const hideQbDesktopConnection = R.compose(
      R.lt(R.__, 1),
      R.length,
      R.defaultTo([]),
    )(qbDesktopConnectionList);

    return {
      [GC.CUSTOM_QUICK_BOOKS_DESKTOP_CONNECTION_CONFIG]: hideQbDesktopConnection,
    };
  },
);

// reference type mapping
const makeSelectInboundMappingReferenceTypeList = () => createSelector(
  selectIntegrationStore,
  ({ inboundMappingReferenceTypeList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(inboundMappingReferenceTypeList),
);

const makeSelectOutboundMappingReferenceTypeList = () => createSelector(
  selectIntegrationStore,
  ({ outboundMappingReferenceTypeList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(outboundMappingReferenceTypeList),
);

// repair shop integration config
const makeSelectRepairShopIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  ({ repairShopIntegrationConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(repairShopIntegrationConfigList),
);

const makeSelectEtaCalculatorConfigList = () => createSelector(
  selectGeneralConfigStore,
  ({ etaCalculatorConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(etaCalculatorConfigList),
);

const makeSelectOutboundAccessorialMappingList = () => createSelector(
  selectIntegrationStore,
  ({ outboundAccessorialMappingList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(outboundAccessorialMappingList),
);

const makeSelectContainerTrackingConfigList = () => createSelector(
  selectIntegrationStore,
  ({ containerTrackingConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(containerTrackingConfigList),
);

const makeSelectCRMIntegrationConfigList = () => createSelector(
  selectIntegrationStore,
  ({ crmIntegrationConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(crmIntegrationConfigList),
);

// service mapping
const makeSelectInboundMappingServiceConfigList = () => createSelector(
  selectIntegrationStore,
  ({ inboundMappingServiceConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(inboundMappingServiceConfigList),
);

const makeSelectOutboundMappingServiceConfigList = () => createSelector(
  selectIntegrationStore,
  ({ outboundMappingServiceConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(outboundMappingServiceConfigList),
);

// order type mapping
const makeSelectInboundMappingOrderTypeConfigList = () => createSelector(
  selectCLOStore,
  ({ inboundMappingOrderTypeConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(inboundMappingOrderTypeConfigList),
);

const makeSelectFreightSafetyConfigList = () => createSelector(
  selectIntegrationStore,
  ({ freightSafetyConfigList }) => R.compose(
    R.sortBy(R.prop('index')),
    R.values,
  )(freightSafetyConfigList),
);

// geofencing zone color config
const makeSelectGeofencingZoneColorConfig = () => createSelector(
  selectUIConfigStore,
  ({ geofencingZoneColorConfig }) => R.or(geofencingZoneColorConfig, {}),
);

export {
  selectConfigStore,
  makeSelectFinancial,
  makeSelectTabsVisited,
  makeSelectDocumentList,
  makeSelectConfigByName,
  makeSelectFuelCardList,
  makeSelectTaskTypeList,
  makeSelectConfigWarning,
  makeSelectConnectedToQB,
  makeSelectDocumentTypes,
  makeSelectConfigOptions,
  makeSelectDataSourceList,
  makeSelectTollChargeList,
  makeSelectEdiConfigsList,
  makeSelectDocumentFilter,
  makeSelectTaskStatusList,
  makeSelectServicesOptions,
  makeSelectConfigDropdowns,
  makeSelectPublicCLOConfig,
  makeSelectExpenseTypeList,
  makeSelectCarrierSequences,
  makeSelectCurrentGroupName,
  makeSelectAccessorialsList,
  makeSelectGLCodeMappingList,
  makeSelectMailTemplateTypes,
  makeSelectMailTemplateByKey,
  makeSelectMailTemplateNames,
  makeSelectActiveGroupWithTab,
  makeSelectMailTemplateConfig,
  makeSelectLosPricingPlanList,
  makeSelectGpsIntegrationList,
  makeSelectAdvancePaymentList,
  makeSelectCommunicationMails,
  makeSelectStatusMessagesList,
  makeSelectServiceTypeOptions,
  makeSelectReasonCodesOptions,
  makeSelectGLCodeMappingFilter,
  makeSelectConfigInitialValues,
  makeSelectLoadBoardConfigList,
  makeSelectVinLookupConfigList,
  makeSelectDocumentSendingList,
  makeSelectServiceVendorOptions,
  makeSelectReasonsForStatusCode,
  makeSelectQBAccountMappingList,
  makeSelectNotificationTriggers,
  makeSelectServiceMappingFilter,
  makeSelectFilteredDocumentList,
  makeSelectAccountingConfigList,
  makeSelectDeactivationRuleList,
  makeSelectTruckTypeMappingList,
  makeSelectQBTaxCodeMappingList,
  makeSelectDocumentHubConfigList,
  makeSelectStatusCodeMappingList,
  makeSelectOnboardingPackageList,
  makeSelectLosSubscriptionConfig,
  makeSelectConfigInheritedValues,
  makeSelectIntegrationConfigList,
  makeSelectIndexedDataSourceList,
  makeSelectIntegrationMailConfig,
  makeSelectEdiExporterConfigList,
  makeSelectConfigGuidsToItemsMap,
  makeSelectDriverPayrollStatuses,
  makeSelectInvoiceStatusRuleList,
  makeSelectCustomerIdMappingList,
  makeSelectServiceTypeMappingList,
  makeSelectCarrierInvoiceStatuses,
  makeSelectQBAccountMappingFilter,
  makeSelectEdiExtractorConfigList,
  makeSelectAvailableDocumentTypes,
  makeSelectSageAccountMappingList,
  makeSelectTrailerTypeMappingList,
  makeSelectFreightSafetyConfigList,
  makeSelectEtaCalculatorConfigList,
  makeSelectQbDesktopConnectionList,
  makeSelectDispatchIntegrationList,
  makeSelectCustomerInvoiceStatuses,
  makeSelectDocumentTypeMappingList,
  makeSelectServicesForAccessorials,
  makeSelectInvoiceStatusRuleFilter,
  makeSelectDepartmentIdMappingList,
  makeSelectMarginViolationRuleList,
  makeSelectTerminalIntegrationList,
  makeSelectCRMIntegrationConfigList,
  makeSelectServiceMappingConfigList,
  makeSelectNotificationListTriggers,
  makeSelectSageAccountMappingFilter,
  makeSelectFilteredNotificationList,
  makeSelectQBCustomFieldMappingList,
  makeSelectPaylocityCodeMappingList,
  makeSelectGeofencingZoneColorConfig,
  makeSelectOnboardingIntegrationList,
  makeSelectInvoiceStatusPriorityList,
  makeSelectEquipmentMappingConfigList,
  makeSelectTermsAndConditionsDocument,
  makeSelectCarrierEdiLoaderConfigList,
  makeSelectCarrierScoreCardConfigList,
  makeSelectNotificationTriggersFilter,
  makeSelectLoadCustomStatusConfigList,
  makeSelectQbIffAssessorialMappingList,
  makeSelectDefaultOrderAccessorialList,
  makeSelectCloDispatchBoardActionPanel,
  makeSelectFleetServiceInvoiceStatuses,
  makeSelectTelDispatchBoardActionPanel,
  makeSelectStatusReasonCodeMappingList,
  makeSelectContainerTrackingConfigList,
  makeSelectNotificationObjectTypeFilter,
  makeSelectCarrierEdiExporterConfigList,
  makeSelectDistanceCalculatorConfigList,
  makeSelectCarrierSynchronizationConfig,
  makeSelectHidiByConfigMapForAuthWrapper,
  makeSelectDefaultCarrierAccessorialList,
  makeSelectStatusCodeOutboundMappingList,
  makeSelectOnboardingPackageDocumentList,
  makeSelectCentralizedDispatchActionPanel,
  makeSelectCarrierIntegrationOverrideList,
  makeSelectFactoringIntegrationConfigList,
  makeSelectCarrierPickupRequesterListList,
  makeSelectTransportationModeGroupingList,
  makeSelectOutboundAccessorialMappingList,
  makeSelectTrailerTrackingIntegrationList,
  makeSelectUserTermsAndConditionsDocument,
  makeSelectFuelCardTransactionMappingList,
  makeSelectPropelHRAssessorialMappingList,
  makeSelectRepairShopIntegrationConfigList,
  makeSelectInboundMappingServiceConfigList,
  makeSelectOutboundDocumentTypeMappingList,
  makeSelectOutboundMappingServiceConfigList,
  makeSelectFilteredServiceMappingConfigList,
  makeSelectCarrierRateIntegrationConfigList,
  makeSelectCrossBorderIntegrationConfigList,
  makeSelectInboundMappingOrderTypeConfigList,
  makeSelectFleetDocumentAndCertificationTypes,
  makeSelectStatusReasonCodeOutboundMappingList,
  makeSelectUserMonitoringIntegrationConfigList,
  makeSelectTransportationModeConfigMappingList,
  makeSelectOrderQuoteTermsAndConditionsDocument,
  makeSelectTruckEquipmentServiceAutoCreateConfigList,
  makeSelectTrailerEquipmentServiceAutoCreateConfigList,
  makeSelectDriverAppAcceptRateTermsAndConditionsDocument,
  makeSelectIntegrationTransportationModeConfigMappingList,
  makeSelectDriverAppAcceptPayrollTermsAndConditionsDocument,
  // reference type mapping
  makeSelectInboundMappingReferenceTypeList,
  makeSelectOutboundMappingReferenceTypeList,
};
